import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { companyInfo, submitContactForm } from '../data/mock';

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCaptchaClick = () => {
    // Mock captcha verification for frontend
    setTimeout(() => {
      setCaptchaVerified(true);
    }, 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!captchaVerified) {
      alert('Por favor verifica que no eres un robot');
      return;
    }

    setIsSubmitting(true);

    try {
      // Using mock function for now
      const response = await submitContactForm(formData);
      
      if (response.success) {
        setIsSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setCaptchaVerified(false);

        // Reset success message after 5 seconds
        setTimeout(() => {
          setIsSuccess(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Hubo un error al enviar el formulario. Por favor intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Teléfono',
      value: companyInfo.phone,
      href: `tel:${companyInfo.phone}`,
      color: 'sky'
    },
    {
      icon: Mail,
      title: 'Email',
      value: companyInfo.email,
      href: `mailto:${companyInfo.email}`,
      color: 'blue'
    },
    {
      icon: MapPin,
      title: 'Ubicación',
      value: 'Región de Chile',
      href: null,
      color: 'cyan'
    }
  ];

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#01A9DB]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#F58020]/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Solicita tu Cotización
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#01A9DB] to-[#F58020] mx-auto rounded-full mb-6"></div>
          <p className="text-lg text-slate-600">
            Completa el formulario y nos pondremos en contacto contigo a la brevedad
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 max-w-7xl mx-auto">
          {/* Contact Info Cards */}
          <div className="lg:col-span-2 space-y-6">
            {contactInfo.map((info, index) => (
              <Card
                key={index}
                className="p-6 bg-white border-2 border-slate-100 hover:border-cyan-200 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 w-12 h-12 bg-gradient-to-br from-${info.color}-500 to-${info.color}-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300`}>
                    <info.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-slate-500 mb-1">
                      {info.title}
                    </h3>
                    {info.href ? (
                      <a
                        href={info.href}
                        className="text-lg font-bold text-slate-900 hover:text-[#01A9DB] transition-colors"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-lg font-bold text-slate-900">
                        {info.value}
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            ))}

            {/* WhatsApp CTA */}
            <Card className="p-6 bg-gradient-to-br from-green-500 to-green-600 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer group">
              <a
                href={companyInfo.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between"
              >
                <div>
                  <h3 className="text-white font-bold text-lg mb-1">
                    WhatsApp Directo
                  </h3>
                  <p className="text-green-100 text-sm">
                    Respuesta inmediata
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                  <Send className="w-6 h-6 text-white" />
                </div>
              </a>
            </Card>

            {/* Working Hours */}
            <Card className="p-6 bg-slate-900 border-0 shadow-xl">
              <h3 className="text-white font-bold text-lg mb-4">
                Horario de Atención
              </h3>
              <div className="space-y-2 text-slate-300">
                <p>Lunes - Viernes: 9:00 - 18:00</p>
                <p>Sábado: 9:00 - 14:00</p>
                <p className="text-sky-400 font-semibold">¡Disponibilidad 24/7 por WhatsApp!</p>
              </div>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="lg:col-span-3 p-8 bg-white border-2 border-slate-100 shadow-xl">
            {isSuccess && (
              <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-xl flex items-start space-x-3 animate-in fade-in slide-in-from-top duration-500">
                <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-green-900 mb-1">¡Mensaje Enviado!</h4>
                  <p className="text-green-700">Gracias por contactarnos. Te responderemos pronto.</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700 font-semibold">
                    Nombre Completo *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="h-12 border-2 border-slate-200 focus:border-[#01A9DB] transition-colors"
                    placeholder="Juan Pérez"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-semibold">
                    Correo Electrónico *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="h-12 border-2 border-slate-200 focus:border-[#01A9DB] transition-colors"
                    placeholder="[email protected]"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700 font-semibold">
                    Teléfono *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="h-12 border-2 border-slate-200 focus:border-[#01A9DB] transition-colors"
                    placeholder="+56 9 1234 5678"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-slate-700 font-semibold">
                    Servicio de Interés *
                  </Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="h-12 border-2 border-slate-200 focus:border-[#01A9DB] transition-colors"
                    placeholder="Ej: Aseo Industrial"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-slate-700 font-semibold">
                  Mensaje *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="border-2 border-slate-200 focus:border-sky-500 transition-colors resize-none"
                  placeholder="Describe tu proyecto o necesidad..."
                />
              </div>

              {/* Mock Human Verification */}
              <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-6">
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={handleCaptchaClick}
                    disabled={captchaVerified}
                    className={`flex-shrink-0 w-8 h-8 border-2 rounded transition-all duration-300 flex items-center justify-center ${
                      captchaVerified
                        ? 'bg-green-500 border-green-500'
                        : 'bg-white border-slate-300 hover:border-sky-500 cursor-pointer'
                    }`}
                  >
                    {captchaVerified && (
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    )}
                  </button>
                  <div className="flex-1">
                    <p className="text-slate-700 font-medium">
                      {captchaVerified ? '✓ Verificado' : 'No soy un robot'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Verificación humana requerida
                    </p>
                  </div>
                  <div className="text-xs text-slate-400">
                    reCAPTCHA
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !captchaVerified}
                className="w-full h-14 bg-gradient-to-r from-[#01A9DB] to-[#F58020] hover:from-[#0891b2] hover:to-[#ea580c] text-white text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    Enviar Solicitud
                    <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};
